import React from 'react';

import Button from 'components/ButtonDeprecated';
import Buttons from 'components/Buttons';
import Chat from 'entities/Chat';
import ChatActions from 'chat/actions/ChatActions';
import { ChatMessage } from 'entities/ChatMessage';
import CurrentUser from 'auth/CurrentUser';
import ConversationViewComposer from 'chat/components/ConversationViewComposer';
import {
  ChatStream,
  addStreamSequenceItem,
  removeStreamSequenceItem,
  lastStreamItemSequenceNumber,
  // @ts-ignore
} from 'chat/components/ChatStream';
// @ts-ignore
import ChatMessageActions from 'chat/actions/ChatMessageActions';
import EventTracking from 'lib/EventTracking';
import Icon from 'components/Icon';
import MediaObject from 'components/MediaObject';
import SingleChatStreamViewSidebar from 'chat/components/SingleChatStreamViewSidebar';
import Text from 'components/Text';
import ToastActions from 'actions/ToastActions';
// @ts-ignore
import UnreadStore from 'stores/UnreadStore';
import { MessageTranslationsInput } from 'globalTypes';
import { V2File } from 'clients/api/fileUploads';

import './styles/SingleChatStreamView.scss';

export type SingleChatStreamViewMember = {
  displayName?: string;
  initials?: string;
  profilePictureUrl?: string;
  role?: string;
};

type ViewHeaderProps = {
  title: string;
  subtext?: string;
  headerActions?: React.ReactElement[];
  onToggleSidebar: () => void;
};

function SingleChatStreamViewHeader({
  title,
  subtext,
  headerActions,
  onToggleSidebar,
}: ViewHeaderProps) {
  const titleComponent = (
    <div>
      <Text size="large" element="div" truncate weight="semibold">
        {title}
      </Text>
      {subtext && (
        <Text size="xsmall" element="div" color="graphite" truncate>
          {subtext}
        </Text>
      )}
    </div>
  );

  return (
    <div className="SingleChatStreamViewHeader">
      <MediaObject figure="right" className="SingleChatStreamViewHeader-top">
        <div className="SingleChatStreamViewHeader-left">
          <div className="SingleChatStreamViewHeader-left-group">{titleComponent}</div>
        </div>
        <div className="SingleChatStreamViewHeader-right">
          <Buttons margin="large" divided>
            <Button level="icon" color="graphite" onClick={onToggleSidebar}>
              <Icon name="info-circle" />
            </Button>
            {headerActions}
          </Buttons>
        </div>
      </MediaObject>
    </div>
  );
}

type Props = {
  streamUuid: string;
  chat: Chat;
  viewEventKey?: string;
  headerActions?: React.ReactElement[];
  chatMemberData: SingleChatStreamViewMember[];
};

type State = {
  sidebarOpen: boolean;
};

export default class SingleChatStreamView extends React.Component<Props, State> {
  state: State = {
    sidebarOpen: false,
  };

  componentDidMount() {
    this.track();
  }

  componentDidUpdate(prevProps: Props) {
    if (
      prevProps.viewEventKey !== this.props.viewEventKey ||
      prevProps.streamUuid !== this.props.streamUuid
    ) {
      this.track();
    }
  }

  toggleSidebar = () => {
    this.setState({ sidebarOpen: !this.state.sidebarOpen });
  };

  track() {
    if (this.props.viewEventKey) {
      EventTracking.trackKey(this.props.viewEventKey);
    }
  }

  render() {
    const { chat, streamUuid, chatMemberData } = this.props;
    function sendMessage(text: string, file?: V2File, translations?: MessageTranslationsInput) {
      const sender = CurrentUser.getUser();
      const seq = lastStreamItemSequenceNumber(chat?.id.toString()) + 1;
      const message = ChatMessage.create(
        text,
        file,
        chat,
        sender,
        seq,
        'text',
        null,
        null,
        sender.getLocale()
      );

      addStreamSequenceItem(chat?.id.toString(), message.toGraphQL());
      ChatMessageActions.sendMessage(message, translations).then(
        () => {},
        (err: Error) => {
          const errorMessage = err.message || 'Error sending message.';
          ToastActions.showErrorToast(errorMessage);
          removeStreamSequenceItem(chat?.id.toString(), message.id);
          console.error('ChatMessageActions.sendMessage failed');
        }
      );
    }

    function updateUnread() {
      const seq = parseInt(lastStreamItemSequenceNumber(chat.id.toString()), 10);

      if (seq) {
        chat.markAsReadForAssociatedGroups(UnreadStore);
        ChatActions.markReadUpto(chat.id, seq);
        UnreadStore.setChatStreamAsRead(streamUuid);
      }
    }

    const chatTitle = chat?.getTitle();
    const chatDescription = chatMemberData?.map(member => member.displayName).join(', ');
    return (
      <div className="SingleChatStreamView">
        <SingleChatStreamViewHeader
          data-test="single-chat-view-header"
          title={chatTitle}
          subtext={chatDescription}
          headerActions={this.props.headerActions}
          onToggleSidebar={this.toggleSidebar}
        />
        <div className="ConversationView">
          <div className="ConversationView-body">
            <div className="ConversationView-body-wrap">
              <ChatStream key={streamUuid} uuid={streamUuid} onScrollToBottom={updateUnread} />
              <ConversationViewComposer
                className="ConversationView-body-composer"
                chat={chat}
                onMessageSend={sendMessage}
              />
            </div>
            {this.state.sidebarOpen && (
              <SingleChatStreamViewSidebar
                data-test="single-chat-view-sidebar"
                chatTitle={chatTitle}
                chatMemberData={chatMemberData}
              />
            )}
          </div>
        </div>
      </div>
    );
  }
}
